import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Terminal, Settings2, Square, RotateCw, X, Bot, Search, Network, List as ListIcon } from "lucide-react";
import { useAgents } from "@/api/queries/agents";
import { useUsers } from "@/api/queries/users";
import { useNodes } from "@/api/queries/nodes";
import { useStopAgent, useRestartAgent } from "@/api/mutations/agents";
import { useAgentsCostSummary } from "@/api/hooks/useCostSummary";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { StatusDot } from "@/components/StatusDot";
import { ListTableShell } from "@/components/ResourceShell";
import { AgentPhaseBadge } from "@/lib/lifecycleBadge";
import { formatUsd, costThreshold } from "@/lib/formatCost";
import { cn } from "@/lib/utils";
import { CreateAgentDialog } from "@/components/agent-actions/CreateAgentDialog";
import { AgentTokenDisplay } from "@/components/agent-actions/AgentTokenDisplay";
import { AgentsGraph } from "@/components/agents-graph/AgentsGraph";

const ROW_LIMIT = 50;
const PROBLEM_PHASES = ["crashed", "crashloopbackoff", "degraded"];
const FILTERS = [
  { v: "", l: "Alle" },
  { v: "running", l: "Läuft" },
  { v: "problem", l: "Problem" },
  { v: "stopped", l: "Gestoppt" },
  { v: "suspended", l: "Suspendiert" },
];

function desiredMeta(d: string): { tone: "ok" | "warn" | "muted"; label: string } {
  if (d === "running") return { tone: "ok", label: "Running" };
  if (d === "suspended") return { tone: "warn", label: "Suspended" };
  return { tone: "muted", label: "Stopped" };
}
function costColor(v: number): string {
  const t = costThreshold(v);
  return t === "alert" ? "text-danger" : t === "warn" ? "text-warn" : "text-text3";
}

function RowAction({ to, title, children }: { to: string; title: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      title={title}
      onClick={(e) => e.stopPropagation()}
      className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-[7px] text-text3 transition-colors hover:bg-surface3 hover:text-text"
    >
      {children}
    </Link>
  );
}

export function AgentsList() {
  const { data, isLoading, isError, error, refetch } = useAgents();
  const users = useUsers();
  const nodes = useNodes();
  const cost = useAgentsCostSummary(data);
  const userMap = new Map((users.data ?? []).map((u) => [u.id, u]));
  const nodeMap = new Map((nodes.data ?? []).map((n) => [n.id, n]));
  const costMap = new Map(cost.data.map((c) => [c.agentId, c.totalCostUsd]));
  const navigate = useNavigate();
  const stopM = useStopAgent();
  const restartM = useRestartAgent();

  const [createOpen, setCreateOpen] = useState(false);
  const [createdToken, setCreatedToken] = useState<{ agentId: string; token: string } | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("q") ?? "";
  const phaseFilter = searchParams.get("phase") ?? "";
  const view = searchParams.get("view") === "graph" ? "graph" : "list";
  function patchParam(key: string, value: string) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  }

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  useEffect(() => {
    if (!data) return;
    const liveIds = new Set(data.map((a) => a.id));
    setSelected((prev) => {
      const next = new Set([...prev].filter((id) => liveIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [data]);
  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const q = search.trim().toLowerCase();
  const filtered = (data ?? []).filter((a) => {
    if (q && !`${a.name} ${a.id}`.toLowerCase().includes(q)) return false;
    if (phaseFilter === "running" && a.lifecyclePhase !== "running") return false;
    if (phaseFilter === "problem" && !PROBLEM_PHASES.includes(a.lifecyclePhase)) return false;
    if (phaseFilter === "stopped" && a.lifecyclePhase !== "stopped") return false;
    if (phaseFilter === "suspended" && a.lifecyclePhase !== "suspended") return false;
    return true;
  });
  const rows = filtered.slice(0, ROW_LIMIT);
  const allVisibleSelected = rows.length > 0 && rows.every((a) => selected.has(a.id));
  function toggleAllVisible() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) rows.forEach((a) => next.delete(a.id));
      else rows.forEach((a) => next.add(a.id));
      return next;
    });
  }

  async function runBulk(action: "stop" | "restart") {
    if (bulkBusy) return;
    setBulkBusy(true);
    try {
      const ids = [...selected];
      const m = action === "stop" ? stopM : restartM;
      const results = await Promise.allSettled(ids.map((id) => m.mutateAsync(id)));
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - ok;
      const verb = action === "stop" ? "gestoppt" : "neugestartet";
      if (failed === 0) toast.success(`${ok} Agent(s) ${verb}`);
      else toast.error(`${ok} ${verb}, ${failed} fehlgeschlagen`);
      setSelected(new Set());
    } finally {
      setBulkBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <p className="text-[13px] text-text3">
          {filtered.length} von {data?.length ?? 0} Agents
        </p>
        <div className="flex items-center gap-2.5">
          <div className="flex gap-1 rounded-lg border border-border bg-surface2 p-[3px]" data-testid="agent-view-toggle">
            {([["list", "Liste", ListIcon], ["graph", "Graph", Network]] as const).map(([v, label, Icon]) => (
              <button
                key={v}
                onClick={() => patchParam("view", v === "list" ? "" : v)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-[6px] px-2.5 py-1.5 text-[12.5px] font-semibold transition-colors",
                  view === v ? "bg-surface text-text shadow-xs" : "text-text3 hover:text-text",
                )}
                data-testid={`agent-view-${v}`}
              >
                <Icon className="h-[15px] w-[15px]" />
                {label}
              </button>
            ))}
          </div>
          <Button onClick={() => setCreateOpen(true)} data-testid="action-create-agent">
            <Plus className="h-4 w-4" />
            Neuer Agent
          </Button>
        </div>
      </div>

      {/* Toolbar / Bulk */}
      {view === "list" && selected.size > 0 ? (
        <div className="flex items-center gap-3 rounded-sm border border-accent-border bg-accent-soft px-3.5 py-2.5" data-testid="bulk-bar">
          <span className="text-[13px] font-bold text-accent-text">{selected.size} ausgewählt</span>
          <Button size="sm" variant="outline" onClick={() => runBulk("stop")} disabled={bulkBusy} data-testid="bulk-stop">
            <Square className="h-4 w-4" />
            Stoppen
          </Button>
          <Button size="sm" variant="outline" onClick={() => runBulk("restart")} disabled={bulkBusy} data-testid="bulk-restart">
            <RotateCw className="h-4 w-4" />
            Neustart
          </Button>
          <div className="flex-1" />
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            <X className="h-4 w-4" />
            Auswahl aufheben
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[220px] max-w-[360px] flex-1">
            <Search className="pointer-events-none absolute left-[11px] top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-text3" />
            <input
              value={search}
              onChange={(e) => patchParam("q", e.target.value)}
              placeholder="Agent suchen (Name / ID)…"
              data-testid="list-search"
              className="h-9 w-full rounded-lg border border-border bg-surface pl-[34px] pr-3 text-[13px] text-text outline-none placeholder:text-text3 focus:border-accent-border"
            />
          </div>
          <div className="flex gap-1 rounded-lg border border-border bg-surface2 p-[3px]" data-testid="agent-phase-filter">
            {FILTERS.map((f) => (
              <button
                key={f.v}
                onClick={() => patchParam("phase", f.v)}
                className={cn(
                  "rounded-[6px] px-3 py-1.5 text-[12.5px] font-semibold transition-colors",
                  phaseFilter === f.v ? "bg-surface text-text shadow-xs" : "text-text3 hover:text-text",
                )}
              >
                {f.l}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Inhalt: Liste oder Graph */}
      {view === "graph" ? (
        <AgentsGraph agents={data ?? []} search={search} phaseFilter={phaseFilter} />
      ) : (
      <ListTableShell
        isLoading={isLoading && !data}
        isError={isError}
        errorMessage={(error as Error | undefined)?.message}
        isEmpty={!isLoading && filtered.length === 0}
        emptyMessage={data && data.length > 0 ? "Kein Agent passt zum Filter" : "Noch keine Agents"}
        emptyHint={data && data.length > 0 ? "Suche/Filter anpassen" : "Klick 'Neuer Agent' zum Anlegen"}
        onRetry={() => refetch()}
        rowLimit={ROW_LIMIT}
        totalRows={filtered.length}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-9 pr-0">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                  aria-label="Alle sichtbaren auswählen"
                  data-testid="select-all"
                  className="h-[15px] w-[15px] accent-accent"
                />
              </TableHead>
              <TableHead>Agent</TableHead>
              <TableHead>Typ</TableHead>
              <TableHead>Phase</TableHead>
              <TableHead>Soll</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Node</TableHead>
              <TableHead>Kosten</TableHead>
              <TableHead className="text-right">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((a) => {
              const sel = selected.has(a.id);
              const c = costMap.get(a.id) ?? 0;
              const owner = a.ownerId ? userMap.get(a.ownerId) : null;
              const node = a.assignment ? nodeMap.get(a.assignment.nodeId) : null;
              const dm = desiredMeta(a.desiredRunState);
              return (
                <TableRow
                  key={a.id}
                  data-clickable="true"
                  onClick={() => navigate(`/agents/${a.id}`)}
                  className={sel ? "bg-accent-soft hover:bg-accent-soft" : undefined}
                >
                  <TableCell className="pr-0" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={sel}
                      onChange={() => toggleOne(a.id)}
                      aria-label={`${a.name} auswählen`}
                      data-testid={`select-${a.id}`}
                      className="h-[15px] w-[15px] accent-accent"
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-border bg-surface2 text-text3">
                        <Bot className="h-4 w-4" />
                      </span>
                      <div className="min-w-0">
                        <div className="font-semibold text-text">{a.name}</div>
                        <div className="mono text-[11.5px] text-text3">{a.id}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={a.type === "user-agent" ? "enrolled" : "default"}>
                      {a.type === "user-agent" ? "user" : "service"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {a.revokedAt ? <Badge variant="revoked">Revoked</Badge> : <AgentPhaseBadge phase={a.lifecyclePhase} />}
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5 text-[12.5px] text-text2">
                      <StatusDot tone={dm.tone} />
                      {dm.label}
                    </span>
                  </TableCell>
                  <TableCell>
                    {owner ? (
                      <Link to={`/users/${a.ownerId}`} onClick={(e) => e.stopPropagation()} className="mono text-[12.5px] text-accent-text hover:underline">
                        {owner.email.split("@")[0]}
                      </Link>
                    ) : (
                      <span className="text-text3">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {node ? (
                      <Link to={`/nodes/${a.assignment?.nodeId}`} onClick={(e) => e.stopPropagation()} className="mono text-[12.5px] text-text2 hover:underline">
                        {node.hostname}
                      </Link>
                    ) : (
                      <span className="text-text3">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={cn("mono tnum text-[12.5px]", costColor(c), c > 1 && "font-semibold")}>
                      {c > 0 ? formatUsd(c) : "—"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <RowAction to={`/agents/${a.id}/exec`} title="Exec-Konsole">
                        <Terminal className="h-4 w-4" />
                      </RowAction>
                      <RowAction to={`/agents/${a.id}/config`} title="Config bearbeiten">
                        <Settings2 className="h-4 w-4" />
                      </RowAction>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ListTableShell>
      )}

      {createOpen && <CreateAgentDialog open onOpenChange={setCreateOpen} onCreated={setCreatedToken} />}
      {createdToken && (
        <AgentTokenDisplay
          open
          onOpenChange={(o) => !o && setCreatedToken(null)}
          agentId={createdToken.agentId}
          token={createdToken.token}
        />
      )}
    </div>
  );
}
